import React, { useState, useEffect, useMemo } from 'react';
import {
  Users, Trash2, Mail, RefreshCw, Search, Filter, UserCheck, UserX,
  Calendar, Clock, Shield, MoreVertical, Eye, ChevronDown, Download,
  Bell, BellOff, ArrowUpDown, User, KeyRound, AlertTriangle, X,
  CheckCircle, XCircle, MailCheck, Globe, Activity, Hash, ChevronRight,
  Send, FileText, Sparkles, Loader2, Save, StickyNote, History, Ban, PlayCircle, Moon, UserPlus
} from 'lucide-react';
import { PeopleKpiStrip } from '../../components/people/PeopleKpiStrip';
import { FilterChips } from '../../components/people/FilterChips';
import { downloadCsv, daysSince } from '../../components/people/peopleUtils';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { ActionLogDrawer } from '../../components/people/ActionLogDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagementPage = ({ getAuthHeaders, currentUser }) => {
  const [userAccounts, setUserAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterNewsletter, setFilterNewsletter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [viewUser, setViewUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // New: inbox messaging, admin notes, bulk send
  const [showInboxModal, setShowInboxModal] = useState(null); // user object or 'bulk'
  const [inboxSubject, setInboxSubject] = useState('');
  const [inboxBody, setInboxBody] = useState('');
  const [sendingInbox, setSendingInbox] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [actionLogUser, setActionLogUser] = useState(null); // Admin action log drawer for public users
  const [statusFilter, setStatusFilter] = useState('all');
  const [suspendTarget, setSuspendTarget] = useState(null); // user object or 'bulk'
  const [suspendReason, setSuspendReason] = useState('');

  useEffect(() => { fetchUserAccounts(); }, []);

  const fetchUserAccounts = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/user-accounts`, { headers });
      setUserAccounts(response.data);
    } catch (error) {
      console.error('Error fetching user accounts:', error);
      toast.error('Failed to load user accounts');
    } finally {
      setLoading(false);
    }
  };

  const toggleStaffRole = async (user) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/admin/user-accounts/${user.id}/staff-role`,
        { is_staff_role: !user.is_staff_role }, { headers });
      toast.success(`Staff role ${user.is_staff_role ? 'revoked' : 'granted'} for ${user.username}`);
      fetchUserAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update staff role');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.post(`${BACKEND_URL}/api/admin/user-accounts/${userId}/reset-password`, { new_password: newPassword }, { headers });
      toast.success('Password reset successfully');
      setShowResetPassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.delete(`${BACKEND_URL}/api/admin/user-accounts/${userId}`, { headers });
      toast.success('User account deleted');
      setShowDeleteConfirm(null);
      setSelectedUsers(prev => { const n = new Set(prev); n.delete(userId); return n; });
      fetchUserAccounts();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0) return;
    const headers = getAuthHeaders();
    if (!headers) return;
    let deleted = 0;
    for (const userId of selectedUsers) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/user-accounts/${userId}`, { headers });
        deleted++;
      } catch {}
    }
    toast.success(`${deleted} user(s) deleted`);
    setSelectedUsers(new Set());
    setShowBulkActions(false);
    fetchUserAccounts();
  };

  // Send inbox message to a single user (or bulk if showInboxModal === 'bulk')
  const sendInboxMessage = async () => {
    if (!inboxSubject.trim() || !inboxBody.trim()) {
      toast.error('Subject and message required'); return;
    }
    setSendingInbox(true);
    try {
      const headers = getAuthHeaders();
      if (showInboxModal === 'bulk') {
        const res = await axios.post(`${BACKEND_URL}/api/admin/user-accounts/bulk-send-inbox`, {
          user_ids: Array.from(selectedUsers),
          subject: inboxSubject, body: inboxBody
        }, { headers });
        toast.success(`Delivered to ${res.data.delivered} inbox${res.data.delivered === 1 ? '' : 'es'}`);
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/user-accounts/${showInboxModal.id}/send-inbox`, {
          subject: inboxSubject, body: inboxBody
        }, { headers });
        toast.success(`Delivered to ${showInboxModal.username}'s inbox`);
      }
      setShowInboxModal(null); setInboxSubject(''); setInboxBody('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    }
    setSendingInbox(false);
  };

  const toggleNewsletter = async (user) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/user-accounts/${user.id}/toggle-newsletter`, {}, { headers });
      toast.success(res.data.subscribed ? `${user.username} subscribed` : `${user.username} unsubscribed`);
      fetchUserAccounts();
    } catch { toast.error('Failed to update'); }
  };

  const bulkToggleNewsletter = async (subscribed) => {
    if (selectedUsers.size === 0) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/user-accounts/bulk-newsletter`, {
        user_ids: Array.from(selectedUsers), subscribed
      }, { headers });
      toast.success(`${res.data.affected} user${res.data.affected === 1 ? '' : 's'} ${subscribed ? 'subscribed' : 'unsubscribed'}`);
      setSelectedUsers(new Set());
      fetchUserAccounts();
    } catch { toast.error('Bulk update failed'); }
  };

  const setUserStatus = async (ids, action, reason = '') => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/user-accounts/bulk-status`, { user_ids: ids, action, reason }, { headers: getAuthHeaders() });
      toast.success(`${res.data.affected} account${res.data.affected === 1 ? '' : 's'} ${action === 'suspend' ? 'suspended' : 'reactivated'}`);
      setSuspendTarget(null); setSuspendReason(''); setSelectedUsers(new Set());
      fetchUserAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Status update failed'); }
  };

  const exportUsers = (rows) => {
    if (!rows.length) return toast.error('Nothing to export');
    downloadCsv(rows, ['username', 'email', 'first_name', 'last_name', 'subscribed_to_newsletter', 'is_suspended', 'is_staff_role', 'created_at', 'last_login'], `users-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${rows.length} users`);
  };

  const saveAdminNotes = async () => {
    if (!showNotesModal) return;
    setSavingNotes(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/admin/user-accounts/${showNotesModal.id}/admin-notes`, {
        notes: notesText
      }, { headers });
      toast.success('Notes saved');
      setUserAccounts(prev => prev.map(u => u.id === showNotesModal.id ? { ...u, admin_notes: notesText } : u));
      setShowNotesModal(null); setNotesText('');
    } catch { toast.error('Failed to save notes'); }
    setSavingNotes(false);
  };

  const STATUS_FILTERS = {
    active: u => !u.is_suspended && daysSince(u.last_login) <= 7,
    suspended: u => u.is_suspended,
    never: u => !u.last_login,
    dormant: u => !!u.last_login && daysSince(u.last_login) > 90,
    staff: u => u.is_staff_role,
    new: u => daysSince(u.created_at) <= 30,
  };

  const filteredUsers = useMemo(() => {
    let result = [...userAccounts];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(u =>
        u.username?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.first_name?.toLowerCase().includes(q) ||
        u.last_name?.toLowerCase().includes(q)
      );
    }
    if (filterNewsletter === 'subscribed') result = result.filter(u => u.subscribed_to_newsletter);
    if (filterNewsletter === 'unsubscribed') result = result.filter(u => !u.subscribed_to_newsletter);
    if (STATUS_FILTERS[statusFilter]) result = result.filter(STATUS_FILTERS[statusFilter]);

    if (sortBy === 'newest') result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else if (sortBy === 'oldest') result.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    else if (sortBy === 'name') result.sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
    else if (sortBy === 'recent-login') result.sort((a, b) => new Date(b.last_login || 0) - new Date(a.last_login || 0));
    return result;
  }, [userAccounts, searchQuery, filterNewsletter, sortBy, statusFilter]);

  const stats = useMemo(() => ({
    total: userAccounts.length,
    suspended: userAccounts.filter(u => u.is_suspended).length,
    never: userAccounts.filter(u => !u.last_login).length,
    dormant: userAccounts.filter(u => u.last_login && daysSince(u.last_login) > 90).length,
    staff: userAccounts.filter(u => u.is_staff_role).length,
    new30: userAccounts.filter(u => daysSince(u.created_at) <= 30).length,
    subscribed: userAccounts.filter(u => u.subscribed_to_newsletter).length,
    recent: userAccounts.filter(u => {
      if (!u.last_login) return false;
      return Date.now() - new Date(u.last_login).getTime() < 7 * 86400000;
    }).length,
    thisMonth: userAccounts.filter(u => {
      if (!u.created_at) return false;
      const d = new Date(u.created_at);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length
  }), [userAccounts]);

  const toggleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) setSelectedUsers(new Set());
    else setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
  };

  const toggleSelect = (id) => {
    setSelectedUsers(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A';
  const formatTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
  const getRelativeTime = (d) => {
    if (!d) return 'Never';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(d);
  };

  const getInitials = (u) => {
    const f = u.first_name?.charAt(0) || '';
    const l = u.last_name?.charAt(0) || '';
    return (f + l).toUpperCase() || u.username?.charAt(0)?.toUpperCase() || '?';
  };

  const getAvatarGradient = (username) => {
    const gradients = [
      'from-cyan-500 to-blue-600',
      'from-violet-500 to-purple-600',
      'from-emerald-500 to-teal-600',
      'from-rose-500 to-pink-600',
      'from-amber-500 to-orange-600',
      'from-indigo-500 to-blue-600',
    ];
    const hash = (username || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return gradients[hash % gradients.length];
  };

  if (currentUser?.role !== 'executive' && currentUser?.role !== 'superadmin' && currentUser?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-96" data-testid="access-denied">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">Access restricted to administrators</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-violet-300/70 font-medium mb-1">People · Community accounts</p>
          <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight" data-testid="page-title">
            User <span className="bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent">Accounts</span>
          </h2>
          <p className="text-white/45 text-sm mt-1">Access control, account health and messaging for registered website users.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchUserAccounts} data-testid="refresh-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs text-white/60 border border-white/[0.08] hover:text-white hover:border-white/20 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={() => exportUsers(filteredUsers)} data-testid="export-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-[#0a0a14] bg-gradient-to-r from-violet-400 to-cyan-400 shadow-[0_10px_30px_-10px_rgba(138,43,226,0.6)] hover:-translate-y-0.5 transition-transform">
            <Download className="w-3.5 h-3.5" /> Export {filteredUsers.length !== userAccounts.length ? 'filtered' : 'all'}
          </button>
        </div>
      </div>

      <PeopleKpiStrip testIdPrefix="user-kpi" active={statusFilter === 'all' ? null : statusFilter} onSelect={(id) => setStatusFilter(id || 'all')} items={[
        { id: 'active', label: 'Active (7d)', value: stats.recent, hex: '#10B981', Icon: Activity, sub: `of ${stats.total} accounts` },
        { id: 'new', label: 'New (30d)', value: stats.new30, hex: '#00E5FF', Icon: UserPlus },
        { id: 'suspended', label: 'Suspended', value: stats.suspended, hex: '#F43F5E', Icon: Ban },
        { id: 'never', label: 'Never logged in', value: stats.never, hex: '#F59E0B', Icon: UserX },
        { id: 'dormant', label: 'Dormant 90d+', value: stats.dormant, hex: '#94A3B8', Icon: Moon },
        { id: 'staff', label: 'Staff role', value: stats.staff, hex: '#8A2BE2', Icon: Shield, sub: `${stats.subscribed} on newsletter` },
      ]} />

      {/* Search & Filters */}
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, username, or email..." data-testid="search-users"
            className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-white placeholder-slate-600 focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none transition-all text-sm" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="sort-users"
            className="px-3 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-sm text-white/70 focus:border-cyan-400/40 focus:outline-none cursor-pointer">
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="name">Name A-Z</option>
            <option value="recent-login">Recent Login</option>
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4 space-y-3">
        <FilterChips label="Status" value={statusFilter} onChange={setStatusFilter} testIdPrefix="user-status-chip" options={[
          { value: 'all', label: 'All', count: stats.total },
          { value: 'active', label: 'Active', hex: '#10B981', count: stats.recent },
          { value: 'suspended', label: 'Suspended', hex: '#F43F5E', count: stats.suspended },
          { value: 'never', label: 'Never logged in', hex: '#F59E0B', count: stats.never },
          { value: 'dormant', label: 'Dormant', hex: '#94A3B8', count: stats.dormant },
          { value: 'staff', label: 'Staff role', hex: '#8A2BE2', count: stats.staff },
        ]} />
        <FilterChips label="Newsletter" value={filterNewsletter} onChange={setFilterNewsletter} testIdPrefix="user-newsletter-chip" options={[
          { value: 'all', label: 'Everyone' },
          { value: 'subscribed', label: 'Subscribed', count: stats.subscribed },
          { value: 'unsubscribed', label: 'Not subscribed', count: stats.total - stats.subscribed },
        ]} />
      </div>

      {/* Bulk Actions Bar */}
      {selectedUsers.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex-wrap" data-testid="bulk-actions-bar">
          <CheckCircle className="w-4 h-4 text-cyan-400" />
          <span className="text-sm text-cyan-300 font-medium">{selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected</span>
          <div className="flex-1" />
          <Button onClick={() => { setShowInboxModal('bulk'); setInboxSubject(''); setInboxBody(''); }} size="sm" data-testid="bulk-send-inbox-btn"
            className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/30">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Send to inboxes
          </Button>
          <Button onClick={() => bulkToggleNewsletter(true)} size="sm" data-testid="bulk-subscribe-btn"
            className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30">
            <Bell className="w-3.5 h-3.5 mr-1.5" /> Subscribe
          </Button>
          <Button onClick={() => bulkToggleNewsletter(false)} size="sm" data-testid="bulk-unsubscribe-btn"
            className="bg-slate-700/40 hover:bg-slate-700/60 text-slate-300 border border-slate-600">
            <BellOff className="w-3.5 h-3.5 mr-1.5" /> Unsubscribe
          </Button>
          <Button onClick={() => { setSuspendTarget('bulk'); setSuspendReason(''); }} size="sm" data-testid="bulk-suspend-btn"
            className="bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-400/30">
            <Ban className="w-3.5 h-3.5 mr-1.5" /> Suspend
          </Button>
          <Button onClick={() => setUserStatus(Array.from(selectedUsers), 'unsuspend')} size="sm" data-testid="bulk-unsuspend-btn"
            className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-400/30">
            <PlayCircle className="w-3.5 h-3.5 mr-1.5" /> Reactivate
          </Button>
          <Button onClick={() => exportUsers(userAccounts.filter(u => selectedUsers.has(u.id)))} size="sm" data-testid="bulk-export-btn"
            className="bg-white/[0.05] hover:bg-white/[0.08] text-white/70 border border-white/[0.1]">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export
          </Button>
          <Button onClick={() => setShowBulkActions(true)} size="sm" data-testid="bulk-delete-btn"
            className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
          </Button>
          <button onClick={() => setSelectedUsers(new Set())} className="text-slate-500 hover:text-slate-300 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden" data-testid="users-table">
        {/* Table Header */}
        <div className="grid grid-cols-[auto_2fr_2fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3 border-b border-white/[0.06] text-[9px] font-semibold text-white/40 uppercase tracking-[0.18em]">
          <div className="flex items-center">
            <input type="checkbox" checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
              onChange={toggleSelectAll} data-testid="select-all-checkbox"
              className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 cursor-pointer" />
          </div>
          <div>User</div>
          <div>Email</div>
          <div>Status</div>
          <div>Joined</div>
          <div>Last Active</div>
          <div>Actions</div>
        </div>

        {/* Table Body */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 rounded-full border-2 border-slate-700 border-t-cyan-500 animate-spin" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="no-users">
            <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-slate-600" />
            </div>
            <p className="text-slate-400 font-medium mb-1">
              {searchQuery ? 'No matching users' : 'No registered users yet'}
            </p>
            <p className="text-slate-600 text-sm">
              {searchQuery ? 'Try a different search term' : 'Users will appear here when they sign up'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800/40">
            {filteredUsers.map((user) => (
              <div key={user.id} data-testid={`user-row-${user.id}`}
                className={`grid grid-cols-[auto_2fr_2fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3.5 items-center hover:bg-white/[0.025] transition-colors group ${selectedUsers.has(user.id) ? 'bg-cyan-500/[0.04]' : ''} ${user.is_suspended ? 'opacity-60' : ''}`}>
                {/* Checkbox */}
                <div>
                  <input type="checkbox" checked={selectedUsers.has(user.id)} onChange={() => toggleSelect(user.id)}
                    data-testid={`select-user-${user.id}`}
                    className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 cursor-pointer" />
                </div>

                {/* User Info */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${getAvatarGradient(user.username)} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                    <span className="text-white text-xs font-bold">{getInitials(user)}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{user.first_name} {user.last_name}</p>
                    <p className="text-slate-500 text-xs truncate">@{user.username}</p>
                  </div>
                </div>

                {/* Email */}
                <div className="min-w-0">
                  <p className="text-slate-300 text-sm truncate">{user.email}</p>
                </div>

                {/* Newsletter Status */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {user.subscribed_to_newsletter ? (
                    <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-medium">
                      <Bell className="w-3 h-3 mr-1" /> Subscribed
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-800 text-slate-500 border border-slate-700 text-[10px] font-medium">
                      <BellOff className="w-3 h-3 mr-1" /> None
                    </Badge>
                  )}
                  {user.is_suspended && (
                    <Badge className="bg-rose-500/15 text-rose-300 border border-rose-400/30 text-[10px] font-semibold" data-testid={`user-suspended-badge-${user.id}`}>
                      <Ban className="w-3 h-3 mr-1" /> Suspended
                    </Badge>
                  )}
                  {user.is_staff_role && (
                    <Badge className="bg-violet-500/15 text-violet-300 border border-violet-500/30 text-[10px] font-semibold">
                      <Shield className="w-3 h-3 mr-1" /> Staff
                    </Badge>
                  )}
                </div>

                {/* Joined */}
                <div>
                  <p className="text-slate-400 text-xs">{formatDate(user.created_at)}</p>
                </div>

                {/* Last Active */}
                <div>
                  <p className={`text-xs flex items-center gap-1.5 ${!user.last_login ? 'text-amber-300/70' : daysSince(user.last_login) > 90 ? 'text-white/30' : 'text-slate-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${!user.last_login ? 'bg-amber-400' : daysSince(user.last_login) <= 7 ? 'bg-emerald-400' : daysSince(user.last_login) > 90 ? 'bg-slate-600' : 'bg-cyan-400/60'}`} />
                    {getRelativeTime(user.last_login)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setViewUser(user)} data-testid={`view-user-${user.id}`}
                    className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-500 hover:text-cyan-400 transition-colors" title="View Details">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => setActionLogUser(user)} data-testid={`action-log-${user.id}`}
                    className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-500 hover:text-cyan-300 transition-colors" title="Admin action log">
                    <History className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setShowInboxModal(user); setInboxSubject(''); setInboxBody(''); }}
                    data-testid={`send-inbox-${user.id}`}
                    className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-500 hover:text-cyan-300 transition-colors" title="Send to inbox">
                    <Send className="w-4 h-4" />
                  </button>
                  <button onClick={() => toggleNewsletter(user)} data-testid={`toggle-newsletter-${user.id}`}
                    className={`p-1.5 rounded-lg transition-colors ${user.subscribed_to_newsletter ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25' : 'hover:bg-slate-700/50 text-slate-500 hover:text-emerald-400'}`}
                    title={user.subscribed_to_newsletter ? 'Unsubscribe from newsletter' : 'Subscribe to newsletter'}>
                    {user.subscribed_to_newsletter ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => { setShowNotesModal(user); setNotesText(user.admin_notes || ''); }}
                    data-testid={`notes-${user.id}`}
                    className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-500 hover:text-amber-300 transition-colors" title="Admin notes">
                    <StickyNote className="w-4 h-4" />
                  </button>
                  <button onClick={() => toggleStaffRole(user)} data-testid={`toggle-staff-role-${user.id}`}
                    className={`p-1.5 rounded-lg transition-colors ${user.is_staff_role ? 'bg-violet-500/15 text-violet-300 hover:bg-violet-500/25' : 'hover:bg-slate-700/50 text-slate-500 hover:text-violet-400'}`}
                    title={user.is_staff_role ? 'Revoke staff moderator role' : 'Grant staff moderator role'}>
                    <Shield className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setShowResetPassword(user); setNewPassword(''); }} data-testid={`reset-pwd-${user.id}`}
                    className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-500 hover:text-amber-400 transition-colors" title="Reset Password">
                    <KeyRound className="w-4 h-4" />
                  </button>
                  {user.is_suspended ? (
                    <button onClick={() => setUserStatus([user.id], 'unsuspend')} data-testid={`unsuspend-user-${user.id}`}
                      className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-slate-500 hover:text-emerald-300 transition-colors" title="Reactivate account">
                      <PlayCircle className="w-4 h-4" />
                    </button>
                  ) : (
                    <button onClick={() => { setSuspendTarget(user); setSuspendReason(''); }} data-testid={`suspend-user-${user.id}`}
                      className="p-1.5 rounded-lg hover:bg-rose-500/10 text-slate-500 hover:text-rose-300 transition-colors" title="Suspend account">
                      <Ban className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => setShowDeleteConfirm(user)} data-testid={`delete-user-${user.id}`}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-colors" title="Delete User">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table Footer */}
        {filteredUsers.length > 0 && (
          <div className="px-5 py-3 bg-slate-900/40 border-t border-slate-800/40 flex items-center justify-between">
            <p className="text-xs text-slate-600">
              Showing {filteredUsers.length} of {userAccounts.length} user{userAccounts.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* View User Modal */}
      <Dialog open={!!viewUser} onOpenChange={() => setViewUser(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="view-user-modal">
          {viewUser && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getAvatarGradient(viewUser.username)} flex items-center justify-center shadow-lg`}>
                    <span className="text-white font-bold text-lg">{getInitials(viewUser)}</span>
                  </div>
                  <div>
                    <p className="text-lg font-semibold">{viewUser.first_name} {viewUser.last_name}</p>
                    <p className="text-sm text-slate-500 font-normal">@{viewUser.username}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Mail, label: 'Email', value: viewUser.email },
                    { icon: Hash, label: 'User ID', value: viewUser.id?.slice(-8) },
                    { icon: Calendar, label: 'Joined', value: formatDate(viewUser.created_at) },
                    { icon: Clock, label: 'Last Login', value: viewUser.last_login ? `${formatDate(viewUser.last_login)} ${formatTime(viewUser.last_login)}` : 'Never' },
                  ].map((item, i) => (
                    <div key={i} className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <item.icon className="w-3.5 h-3.5 text-slate-500" />
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{item.label}</p>
                      </div>
                      <p className="text-sm text-white truncate">{item.value}</p>
                    </div>
                  ))}
                </div>

                <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-300">Newsletter Subscription</span>
                  </div>
                  {viewUser.subscribed_to_newsletter ? (
                    <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs">Active</Badge>
                  ) : (
                    <Badge className="bg-slate-700 text-slate-400 border border-slate-600 text-xs">Inactive</Badge>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={() => { setViewUser(null); setShowResetPassword(viewUser); setNewPassword(''); }}
                    variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800">
                    <KeyRound className="w-4 h-4 mr-1.5" /> Reset Password
                  </Button>
                  <Button onClick={() => { setViewUser(null); setShowDeleteConfirm(viewUser); }}
                    variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={!!showResetPassword} onOpenChange={() => { setShowResetPassword(null); setNewPassword(''); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="reset-password-modal">
          {showResetPassword && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-400">
                  <KeyRound className="w-5 h-5" /> Reset Password
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-3">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getAvatarGradient(showResetPassword.username)} flex items-center justify-center`}>
                    <span className="text-white text-xs font-bold">{getInitials(showResetPassword)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{showResetPassword.first_name} {showResetPassword.last_name}</p>
                    <p className="text-xs text-slate-500">@{showResetPassword.username}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">New Password</label>
                  <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password-input"
                    className="w-full px-4 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none text-sm"
                    placeholder="Min 6 characters" />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={() => { setShowResetPassword(null); setNewPassword(''); }}
                    variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800">Cancel</Button>
                  <Button onClick={() => handleResetPassword(showResetPassword.id)} size="sm" data-testid="confirm-reset-btn"
                    className="flex-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/20">
                    <KeyRound className="w-4 h-4 mr-1.5" /> Reset
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="delete-confirm-modal">
          {showDeleteConfirm && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-400">
                  <AlertTriangle className="w-5 h-5" /> Delete User Account
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-3">
                <p className="text-sm text-slate-400">
                  Are you sure you want to permanently delete the account for
                  <span className="text-white font-medium"> {showDeleteConfirm.first_name} {showDeleteConfirm.last_name}</span>?
                  This action cannot be undone.
                </p>
                <div className="flex gap-2 pt-1">
                  <Button onClick={() => setShowDeleteConfirm(null)}
                    variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800">Cancel</Button>
                  <Button onClick={() => handleDeleteUser(showDeleteConfirm.id)} size="sm" data-testid="confirm-delete-btn"
                    className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <Dialog open={showBulkActions} onOpenChange={setShowBulkActions}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="bulk-delete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" /> Bulk Delete Users
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <p className="text-sm text-slate-400">
              You are about to permanently delete <span className="text-white font-medium">{selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''}</span>.
              This cannot be undone.
            </p>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowBulkActions(false)}
                variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800">Cancel</Button>
              <Button onClick={handleBulkDelete} size="sm" data-testid="confirm-bulk-delete-btn"
                className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Send Inbox Modal (single or bulk) */}
      <Dialog open={!!showInboxModal} onOpenChange={() => setShowInboxModal(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="send-inbox-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-300">
              <Send className="w-5 h-5" />
              {showInboxModal === 'bulk' ? `Send to ${selectedUsers.size} inbox${selectedUsers.size === 1 ? '' : 'es'}` : `Send to ${showInboxModal?.username}'s inbox`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Subject</label>
              <input value={inboxSubject} onChange={(e) => setInboxSubject(e.target.value)}
                placeholder="A short, clear subject line"
                data-testid="inbox-subject-input"
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm focus:border-cyan-500/50 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Message</label>
              <textarea value={inboxBody} onChange={(e) => setInboxBody(e.target.value)}
                rows={6} placeholder="What do you want them to see? This lands in their in-app inbox — no email."
                data-testid="inbox-body-input"
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm focus:border-cyan-500/50 focus:outline-none resize-none" />
              <p className="text-[10px] text-slate-500 mt-1">{inboxBody.length}/4000</p>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowInboxModal(null)} variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800">Cancel</Button>
              <Button onClick={sendInboxMessage} disabled={sendingInbox || !inboxSubject.trim() || !inboxBody.trim()}
                data-testid="inbox-send-btn"
                className="flex-1 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/30">
                {sendingInbox ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Deliver
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Notes Modal */}
      <Dialog open={!!showNotesModal} onOpenChange={() => setShowNotesModal(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="admin-notes-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-300">
              <StickyNote className="w-5 h-5" />
              Notes on @{showNotesModal?.username}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <p className="text-xs text-slate-500">Private — only staff with admin access can see this. Great for context on tricky accounts.</p>
            <textarea value={notesText} onChange={(e) => setNotesText(e.target.value)}
              rows={8} placeholder="e.g. 'VIP customer. Prefers email replies after 5pm GMT.'"
              data-testid="admin-notes-input"
              className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm focus:border-amber-500/50 focus:outline-none resize-none" />
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowNotesModal(null)} variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800">Cancel</Button>
              <Button onClick={saveAdminNotes} disabled={savingNotes} data-testid="save-notes-btn"
                className="flex-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30">
                {savingNotes ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save notes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Suspend user modal */}
      <Dialog open={!!suspendTarget} onOpenChange={() => setSuspendTarget(null)}>
        <DialogContent className="bg-[#0b0b16] border-white/[0.08] text-white max-w-sm" data-testid="suspend-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-300"><Ban className="w-5 h-5" />
              Suspend {suspendTarget === 'bulk' ? `${selectedUsers.size} accounts` : `@${suspendTarget?.username}`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/55">Suspended users can't sign in until reactivated. Their data is kept.</p>
          <textarea value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} rows={3} data-testid="suspend-user-reason"
            placeholder="Reason (optional) — e.g. spam, abuse report, chargeback…"
            className="w-full px-3 py-2.5 bg-black/40 border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-rose-400/40 resize-none" />
          <div className="flex gap-2">
            <Button onClick={() => setSuspendTarget(null)} variant="outline" size="sm" className="flex-1 border-white/[0.1] bg-transparent text-white/60">Cancel</Button>
            <Button onClick={() => setUserStatus(suspendTarget === 'bulk' ? Array.from(selectedUsers) : [suspendTarget.id], 'suspend', suspendReason)}
              size="sm" data-testid="confirm-suspend-user" className="flex-1 bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-400/30">
              <Ban className="w-4 h-4 mr-1.5" /> Suspend
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default UserManagementPage;
